import React, { FC, useEffect, useState } from "react";
import Network from "../../../utils/Network";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GuildJoinTypeEnum, GuildPlayerStateEnum, GuildType } from "../types";
import { PlayerProfile } from "../../../modules/player-profile/types";
import ThousandsSeparator from "../../../utils/thousands-separator";
import { formatNumber } from "../../../utils/format-number";
import { GuildJoinValues, GuildPlayerState } from "../data";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import useToast from "../../../hooks/useToast";
import Button from "../../../components/Button";
import { CustomIcon } from "../../../components/Icons";
import GuildMemberBox from "../../../components/GuildMemberBox";
import Dialog from "../../../components/Dialog";
import { Form } from "react-bootstrap";
import { setUserInfo } from "../../../redux/user/action";
import { ConfirmDialogType } from "../../../types/global";
import CircularProgress from "../../../components/CircularProgress";
import GuildCrest from "../components/GuildCrest";
import { GuildMember, groupMembers, isSeated } from "../utils";
import { chatsSlice } from "../../../modules/chat/store/chatSlice";
import {
  getGroupByExternalId,
  getGroupChannel,
} from "../../../modules/chat/store/chatActions";
import { useChatDispatch } from "../../../modules/chat/hooks/reduxHook";
import { ConversationTypeEnum } from "../../../modules/chat/types/enums";
import { ChatListType } from "../../../modules/chat/types";
import useSelectChat from "../../../modules/chat/hooks/useSelectChat";
import { getInfoByType } from "../../../modules/chat/functions";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

const { actions } = chatsSlice;

type Props = {
  id: number;
  /** Told which hall this guild has, once it is known, so the page can stand in it. */
  onHall?: (banner: string | null) => void;
};

type GuildDetail = {
  guild: GuildType;
  members: GuildMember[];
  stateInGuild?: GuildPlayerStateEnum;
};
const GuildDetail: FC<Props> = ({ id, onHall }) => {
  const { t } = useAppTranslation(["guild", "common"]);
  const axios = new Network();
  const [data, setData] = useState<GuildDetail>();
  const { userInfo, conversationListByType } = useSelector((state: any) => {
    return {
      userInfo: state?.userInfo,
      conversationListByType: state.chats.conversationListByType,
    };
  }, shallowEqual);

  const dispatch = useDispatch();
  const chatDispatch = useChatDispatch();
  const openToast = useToast();
  const { handleSelectChat } = useSelectChat();

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogType>({
    id: "0",
    open: false,
    submit: () => console.log(),
    title: "",
    msg: "",
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [changeRoleDialog, setChangeRoleDialog] = useState({
    open: false,
    state: 1,
    loading: false,
    id: 0,
  });
  const navigate = useNavigate();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const handleGetDetail = async () => {
    // The player's own guild id arrives with the player; there is nothing to ask for before it.
    if (!id) return;
    setLoading(true);
    const response = await axios.CallApi<GuildDetail>({
      url: `/guild/${id}`,
      method: "get",
    });
    if (response.ok && response.data?.guild) {
      setData(response.data);
      onHall?.(response.data.guild.banner || null);
    } else {
      setData(undefined);
    }
    setLoading(false);
  };

  const getMe = async () => {
    const response = await axios.CallApi({
      url: "/player",
      method: "get",
    });
    if (response.ok) {
      dispatch(setUserInfo(response.data));
    }
  };

  const changeRole = async (playerId: number, state: GuildPlayerStateEnum) => {
    setChangeRoleDialog((prev) => ({ ...prev, loading: true }));
    const response = await axios.CallApi({
      method: "get",
      url: `/guild/${id}/change-role?playerIdToChangeRole=${playerId}&newRole=${state}`,
    });
    openToast(response);
    setChangeRoleDialog((prev) => ({ ...prev, loading: false, open: false }));
    if (response.ok) {
      handleGetDetail();
      getMe();
    }
  };
  const changePlayerRole = async (
    playerId: number,
    state: GuildPlayerStateEnum
  ) => {
    setActionLoading(true);
    const response = await axios.CallApi({
      method: "get",
      url: `/guild/${id}/change-role?playerIdToChangeRole=${playerId}&newRole=${state}`,
    });
    openToast(response);
    setActionLoading(false);
    setConfirmDialog((prev) => ({ ...prev, open: false }));
    // Refreshed either way: a refusal usually means the list is out of date (the request has
    // expired because the player joined another guild, or the guild filled up meanwhile).
    handleGetDetail();
  };

  const handleKick = async (playerId: number) => {
    setActionLoading(true);
    const response = await axios.CallApi({
      method: "get",
      url: `/guild/${id}/kick?playerIdToKick=${playerId}`,
    });
    openToast(response);
    setActionLoading(false);
    if (response.ok) {
      handleGetDetail();
      setConfirmDialog((prev) => ({ ...prev, open: false }));
    }
  };

  const handleLeave = async () => {
    setActionLoading(true);
    const response = await axios.CallApi({
      url: `/guild/${id}/left`,
      method: "get",
    });
    openToast(response);
    if (response.ok) {
      searchParameters.set("tab", "explore");
      setSearchParameters(searchParameters);
      getMe();
    }
    setActionLoading(false);
  };

  const handleAccept = async () => {
    setActionLoading(true);
    const response = await axios.CallApi({
      url: `/guild/${id}/accept`,
      method: "get",
    });
    openToast(response);
    if (response.ok) {
      searchParameters.set("tab", "my-guild");
      setSearchParameters(searchParameters);
      getMe();
    } else {
      // The invitation may have expired, or the guild filled up: show what is true now.
      handleGetDetail();
    }
    setActionLoading(false);
  };

  const handleJoin = async () => {
    setActionLoading(true);
    const response = await axios.CallApi({
      url: `/guild/${id}/join`,
      method: "get",
    });
    openToast(response);
    if (response.ok) {
      searchParameters.set("tab", "my-guild");
      setSearchParameters(searchParameters);
      handleGetDetail();
      getMe();
    }
    setActionLoading(false);
  };

  const handleEdit = () => {
    searchParameters.set("tab", "setting");
    searchParameters.set("id", id + "");
    setSearchParameters(searchParameters);
  };

  useEffect(() => {
    handleGetDetail();
  }, [id]);

  const getGroupGuildByExternalId = () => {
    return new Promise((resolve, reject) => {
      chatDispatch(getGroupByExternalId(id, userInfo?.id))
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const handleChat = () => {
    const conversationList = Object.values(
      conversationListByType
    ).flat() as ChatListType[];
    const conversation = conversationList?.find(
      (conversation) => conversation.external_id === id
    );
    if (conversation) {
      dispatch(actions.setChatState(true));
      handleSelectChat({ chat: getInfoByType(conversation) });
    } else {
      setIsLoadingChat(true);
      getGroupGuildByExternalId()
        .then((conversation: any) => {
          dispatch(actions.setChatState(true));
          handleSelectChat({ chat: conversation as ChatListType });
        })
        .finally(() => setIsLoadingChat(false));
    }
  };

  const isJoined = userInfo?.guild?.id === id;
  const isOwner = data?.guild.ownerId === userInfo?.id;
  const playerRole = data?.stateInGuild;
  // The server keeps every guild to its capacity (10) and a player to one guild; the buttons say so first.
  const seated = data ? data.members.filter(isSeated).length : 0;
  const capacity = data?.guild.capacity ?? 0;
  const isFull = Boolean(data) && seated >= capacity;
  const inAnotherGuild = Boolean(userInfo?.guild?.id) && !isJoined;
  const cannotJoinReason = isJoined
    ? null
    : isFull
    ? t("guild:detail.full", { members: seated, max: capacity })
    : inAnotherGuild
    ? t("guild:detail.inAnotherGuild")
    : null;
  const confirmationTitle = t("guild:detail.dialogs.confirmation.title");
  const confirmationMessage = t("guild:detail.dialogs.confirmation.message");
  const getJoinTypeLabel = (value: string | number) =>
    t(`guild:explore.joinTypes.${value}`, {
      defaultValue: GuildJoinValues[value],
    });
  const MainActions = [
    {
      name: t("guild:detail.actions.chat"),
      action: () => handleChat(),
      loading: actionLoading,
      icon: { name: "chat", type: "svg" },
      variant: "default",
      disabled: isLoadingChat,
      condition: isJoined,
    },
    {
      name: t("guild:detail.actions.setting"),
      action: () => handleEdit(),
      loading: actionLoading,
      variant: "default",
      icon: { name: "settings", type: "svg" },
      disabled: false,
      condition: isJoined && isOwner,
    },
    {
      name: t("guild:detail.actions.leave"),
      action: () =>
        setConfirmDialog((prev) => ({
          ...prev,
          msg: t("guild:detail.confirmations.leave", { name: data?.guild?.name }),
          open: true,
          submit: () => handleLeave().then(() => setConfirmDialog((p) => ({ ...p, open: false }))),
          title: confirmationTitle,
        })),
      loading: actionLoading,
      variant: "secondary",
      icon: { name: "leave", type: "png" },
      disabled: false,
      condition: isJoined,
    },
    {
      name: t("guild:detail.actions.acceptRequest"),
      action: () => handleAccept(),
      loading: actionLoading,
      variant: "wide-success",
      icon: { name: "leave", type: "png" },
      disabled: Boolean(cannotJoinReason),
      condition:
        !isJoined && data?.stateInGuild === GuildPlayerStateEnum.Invited,
    },
    {
      name: t("guild:detail.actions.join"),
      action: () => handleJoin(),
      loading: actionLoading,
      variant: "success",
      disabled: Boolean(cannotJoinReason),
      condition:
        !isJoined &&
        data?.stateInGuild !== GuildPlayerStateEnum.Invited &&
        data?.guild.joinType === GuildJoinTypeEnum.Public,
    },
    {
      name: t("guild:detail.actions.requestMembership"),
      action: () => handleJoin(),
      loading: actionLoading,
      variant: "wide-success",
      disabled: Boolean(cannotJoinReason),
      condition:
        !isJoined &&
        data?.stateInGuild !== GuildPlayerStateEnum.Invited &&
        data?.stateInGuild !== GuildPlayerStateEnum.JoinRequest &&
        data?.guild.joinType === GuildJoinTypeEnum.JoinRequest,
    },
    {
      name: t("guild:detail.actions.requestPending"),
      action: () => handleJoin(),
      variant: "wide-success",
      disabled: true,
      condition:
        data?.stateInGuild === GuildPlayerStateEnum.JoinRequest &&
        data?.guild.joinType === GuildJoinTypeEnum.JoinRequest,
    },
  ] as const;

  if (loading)
    return (
      <div className="gh-status">
        <CircularProgress />
      </div>
    );
  if (!data)
    return (
      <div className="gh-status gh-panel">
        {t("guild:detail.loadFailed")}
        <Button variant="primary" onClick={() => handleGetDetail()}>
          {t("guild:detail.actions.tryAgain")}
        </Button>
      </div>
    );

  const guild = data.guild;
  const askFirst = (submit: () => void, msg: string) =>
    setConfirmDialog((prev) => ({ ...prev, msg, open: true, submit, title: confirmationTitle }));

  return (
    <div>
      <header className="gh-hero">
        <GuildCrest image={guild.image} size="large" level={guild.level} />
        <div>
          <h2 className="gh-hero-name">{guild.name}</h2>
          {guild.description && <p className="gh-hero-about">{guild.description}</p>}
          <ul className="gh-facts">
            <li>
              <img src="/assets/images/icons/earth.png" alt="" />
              {t(`guild:explore.languages.${guild.language}`, { defaultValue: guild.language })}
            </li>
            <li>
              <img src="/assets/images/icons/letter.png" alt="" />
              {getJoinTypeLabel(guild.joinType)}
            </li>
          </ul>
        </div>
        <div className="gh-hero-actions">
          {MainActions.map(
            (action) =>
              action.condition && (
                <Button
                  key={action.name}
                  className="bg-100"
                  variant={action.variant}
                  disabled={action.loading || action.disabled}
                  onClick={action.action || undefined}
                >
                  {action.icon && (
                    <CustomIcon
                      width={25}
                      height={25}
                      className="me-1"
                      icon={action.icon.name}
                      type={action.icon.type}
                    />
                  )}
                  {action.name}
                </Button>
              )
          )}
          {cannotJoinReason && <p className="gh-hero-note">{cannotJoinReason}</p>}
        </div>
      </header>

      <dl className="gh-stats">
        <div className="gh-stat">
          <img src="/assets/images/icons/crown.png" alt="" />
          <div>
            <dt>{t("guild:detail.stats.worth")}</dt>
            <dd>{formatNumber(guild.score)}</dd>
          </div>
        </div>
        <div className="gh-stat">
          <img src="/assets/images/icons/glory.png" alt="" />
          <div>
            <dt>{t("guild:detail.stats.warPower")}</dt>
            <dd>{formatNumber(guild.power)}</dd>
          </div>
        </div>
        <div className="gh-stat">
          <img src="/assets/images/icons/warrior.png" alt="" />
          <div>
            <dt>{t("guild:detail.stats.members")}</dt>
            <dd>
              {seated} / {guild.capacity}
            </dd>
            <div className={isFull ? "gh-meter is-full" : "gh-meter"}>
              <span style={{ width: `${Math.min(100, (seated / Math.max(1, guild.capacity)) * 100)}%` }} />
            </div>
          </div>
        </div>
        <div className="gh-stat">
          <img src="/assets/images/icons/crown-flag.png" alt="" />
          <div>
            <dt>{t("guild:detail.stats.joinScore")}</dt>
            <dd>{ThousandsSeparator(guild.minRankScore || 0)}</dd>
          </div>
        </div>
      </dl>

      <section className="gh-roster" aria-label={t("guild:detail.stats.members")}>
        {groupMembers(data.members).map((group) => (
          <React.Fragment key={group.title}>
            <h3 className="gh-roster-title">
              {t(`guild:detail.roster.${group.state}`, { defaultValue: group.title })}
              {group.members.length > 1 && <span>{group.members.length}</span>}
            </h3>
            {group.state === GuildPlayerStateEnum.JoinRequest && isFull && (
              <p className="gh-roster-note">{t("guild:detail.requestsWaitForSeat", { members: seated, max: capacity })}</p>
            )}
            <div className="gh-roster-list">
              {group.members.map((player) => (
                <GuildMemberBox
                  key={player.playerId}
                  {...player}
                  guildId={id}
                  isOwner
                  playerRole={playerRole!}
                  canAccept={!isFull}
                  onAccept={(playerId) =>
                    askFirst(
                      () => changePlayerRole(playerId, GuildPlayerStateEnum.Member),
                      t("guild:detail.confirmations.accept", { name: player.username })
                    )
                  }
                  onReject={(playerId) =>
                    askFirst(
                      () => changePlayerRole(playerId, GuildPlayerStateEnum.InviteRejected),
                      t("guild:detail.confirmations.reject", { name: player.username })
                    )
                  }
                  onKick={(playerId) =>
                    askFirst(() => handleKick(playerId), t("guild:detail.confirmations.kick", { name: player.username }))
                  }
                  onChangeRole={(state, id) =>
                    setChangeRoleDialog((prev) => ({
                      ...prev,
                      state,
                      id,
                      open: true,
                    }))
                  }
                />
              ))}
            </div>
          </React.Fragment>
        ))}
      </section>

      <Dialog
        loading={actionLoading}
        title={confirmDialog?.title}
        closeLabel={t("common:actions.no")}
        submitLabel={t("common:actions.yes")}
        onSubmit={() => confirmDialog.submit()}
        open={confirmDialog?.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      >
        {confirmDialog?.msg}
      </Dialog>
      <Dialog
        title={t("guild:detail.dialogs.changeRole.title")}
        closeLabel={t("common:actions.cancel")}
        loading={changeRoleDialog.loading}
        submitLabel={t("common:actions.save")}
        onSubmit={() => changeRole(changeRoleDialog.id, changeRoleDialog.state)}
        open={changeRoleDialog.open}
        onClose={() =>
          setChangeRoleDialog((prev) => ({ ...prev, open: false }))
        }
      >
        <div>
          <Form.Select
            value={changeRoleDialog.state}
            onChange={(e: any) => {
              setChangeRoleDialog((prev) => ({
                ...prev,
                state: e.target.value,
              }));
            }}
          >
            <option disabled selected>
              {t("guild:detail.dialogs.changeRole.choose")}
            </option>
            {Object.entries(GuildPlayerState)
              .slice(0, 3)
              .map(([key, value]) => (
                <option value={key}>
                  {t(`guild:detail.roles.${key}`, {
                    defaultValue: value as string,
                  })}
                </option>
              ))}
          </Form.Select>
        </div>
      </Dialog>
    </div>
  );
};

export default GuildDetail;
